import React, { useContext, useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Avatar, 
  Button, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Images, ThumbsUp, Share2, Settings, Award } from 'lucide-react';
import { LoginInfo } from '../App';
import ApiFetch from './ApiFetch';
import Header from './Header';
import Category from './Category';
import Footer from './Footer';
import UserMark from './UserMark';
import { CloudDownload } from 'react-bootstrap-icons';

const UserBest = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const { loginInfo } = useContext(LoginInfo);
  const navigate = useNavigate();
  const { id: userIdOrDisplayId, year } = useParams();

  // State
  const [userId, setUserId] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [bestInfo, setBestInfo] = useState(null);
  const [follow, setFollow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({ title: "", message: "" });

  // API calls
  const fetchUserInfo = async (userIdOrDisplayId) => {
    try {
      const response = await new ApiFetch().get(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/profile/${userIdOrDisplayId}`
      );
      const data = await response.json();
      if (data.result) {
        setUserInfo(data);
        setUserId(data.user_id);
        document.title = `${data.user_name}さんの${year}年振り返り - ourt AI`;
        return data.user_id;
      } else {
        throw new Error(data.errors.join("\n"));
      }
    } catch (error) {
      setError("ユーザー情報の取得に失敗しました");
      return null;
    }
  };

  const fetchBestInfo = async (year, userIdOrDisplayId) => {
    try {
      const response = await new ApiFetch().get(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/profile/best/${userIdOrDisplayId}/${year}`
      );
      const data = await response.json();
      if (data.result) {
        setBestInfo(data.data);
      } else {
        throw new Error("年間データの取得に失敗しました");
      }
    } catch (error) {
      setError("年間データの取得に失敗しました");
    }
  };

  const checkFollowStatus = async (targetUserId) => {
    try {
      const response = await new ApiFetch().get(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/follow/check/${targetUserId}`
      );
      const data = await response.json();
      setFollow(data.result);
    } catch (error) {
      console.error("フォロー状態の確認に失敗しました", error);
    }
  };

  // Follow/Unfollow handlers
  const handleFollow = async () => {
    if (loginInfo.userId === userInfo.user_id) {
      showDialog("エラー", "自分自身はフォローできません");
      return;
    }
    try {
      const response = await new ApiFetch().post(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/follow/${userInfo.user_id}`
      );
      const data = await response.json();
      if (data.result) {
        setFollow(true);
      }
    } catch (error) {
      showDialog("エラー", "フォローに失敗しました");
    }
  };

  const handleUnfollow = () => {
    showDialog("確認", "フォローを解除しますか？", true);
  };

  const executeUnfollow = async () => {
    try {
      const response = await new ApiFetch().delete(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/follow/${userInfo.user_id}`
      );
      const data = await response.json();
      if (data.result) {
        setFollow(false);
      }
    } catch (error) {
      showDialog("エラー", "フォロー解除に失敗しました");
    }
    setDialogOpen(false);
  };

  // Dialog helpers
  const showDialog = (title, message, showConfirm = false) => {
    setDialogConfig({ 
      title, 
      message, 
      showConfirm 
    });
    setDialogOpen(true);
  };

  // Share handler
  const handleShare = () => {
    const text = `${userInfo.user_name}さんの${year}年振り返り\n${ApiFetch.API_PROTOCOL}//ourt-ai.work/user/${userIdOrDisplayId}/best/${year}\n#アワートAI #AIart`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };
  // Download handler
  const handleDownload = () => {
    window.open(`${ApiFetch.API_PROTOCOL}//${ApiFetch.IMG_HOST}/best/${year}/${userInfo.user_id}_best3_ogp.webp`, '_blank');
  };
  // Initial data fetch
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      const userId = await fetchUserInfo(userIdOrDisplayId);
      if (userId) {
        await Promise.all([
          fetchBestInfo(year, userIdOrDisplayId),
          checkFollowStatus(userId)
        ]);
      }
      setLoading(false);
    };

    initializeData();
  }, [userIdOrDisplayId, year]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="wrapper">
      <Header />
      <Category />
      
      <Container maxWidth="lg">
        <Box py={{ xs: 2, sm: 3, md: 4 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
            {/* Profile Header */}
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center" direction={isMobile ? 'column' : 'row'}>
              <Grid item xs={12} sm="auto">
                <Box display="flex" justifyContent={isMobile ? 'center' : 'flex-start'}>
                  <Avatar
                    src={`${ApiFetch.API_PROTOCOL}//${ApiFetch.IMG_HOST}/profile/${userId}.jpg?${loginInfo.hash}`}
                    sx={{ 
                      width: { xs: 80, sm: 100, md: 120 }, 
                      height: { xs: 80, sm: 100, md: 120 },
                      border: 4,
                      borderColor: 'background.paper'
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs>
                <Stack spacing={{ xs: 1, sm: 2 }} alignItems={isMobile ? 'center' : 'flex-start'}>
                  <Box display="flex" 
                       alignItems="center" 
                       gap={1} 
                       flexDirection={isMobile ? 'column' : 'row'}
                       textAlign={isMobile ? 'center' : 'left'}>
                    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                      {userInfo?.user_name}
                    </Typography>
                    <UserMark 
                      sms_auth={userInfo?.sms_auth}
                      role={userInfo?.role}
                      plan={userInfo?.plan}
                      beginner={userInfo?.beginner}
                      identity_confirmed={userInfo?.identity_confirmed}
                      dialog={() => {}}
                    />
                    <Typography variant="subtitle1">
                      さんの{year}年振り返り
                    </Typography>
                  </Box>
                  
                  {userInfo?.isFollowed ? (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        color: 'text.secondary',
                        width: 'fit-content'
                      }}
                    >
                      フォローされています
                    </Typography>
                  ) : ""}
                  
                  <Stack direction={{ xs: 'column', sm: 'row' }} 
                         spacing={2} 
                         width={isMobile ? '100%' : 'auto'}>
                    {userInfo?.user_id !== loginInfo?.userId && userInfo?.user_id !== "anonymous" && (
                      <Button
                        variant={follow ? "outlined" : "contained"}
                        color={follow ? "inherit" : "primary"}
                        onClick={follow ? handleUnfollow : handleFollow}
                        fullWidth={isMobile}
                      >
                        {follow ? 'フォロー済' : 'フォローする'}
                      </Button>
                    )}
                    
                    <Button
                      variant="outlined"
                      startIcon={<Share2 />}
                      onClick={handleShare}
                      fullWidth={isMobile}
                    >
                      シェア
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CloudDownload />}
                      onClick={handleDownload}
                      fullWidth={isMobile}
                    >
                      保存
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            {/* Stats Section */}
            <Paper 
              elevation={0}
              sx={{ 
                mt: { xs: 3, sm: 4 }, 
                p: { xs: 2, sm: 3 }, 
                bgcolor: 'grey.50',
                borderRadius: 2
              }}
            >
              <Grid container 
                    spacing={{ xs: 2, sm: 4 }} 
                    justifyContent="center"
                    direction={isMobile ? 'column' : 'row'}>
                <Grid item xs={12} sm="auto">
                  <Stack alignItems="center" spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Images size={isMobile ? 16 : 20} />
                      <Typography color="text.secondary" variant="body2">
                        投稿数
                      </Typography>
                    </Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                      {bestInfo?.images_count || 0}
                    </Typography>
                  </Stack>
                </Grid>
                
                {!isMobile && <Divider orientation="vertical" flexItem />}
                {isMobile && <Divider />}
                
                <Grid item xs={12} sm="auto">
                  <Stack alignItems="center" spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ThumbsUp size={isMobile ? 16 : 20} />
                      <Typography color="text.secondary" variant="body2">
                        もらった
                      </Typography>
                    </Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                      {bestInfo?.favorite_count || 0}
                    </Typography>
                  </Stack>
                </Grid>
                
                {!isMobile && <Divider orientation="vertical" flexItem />}
                {isMobile && <Divider />}
                
                <Grid item xs={12} sm="auto">
                  <Stack alignItems="center" spacing={1}>
                    <Typography color="text.secondary" variant="body2">
                      👏 もらった
                    </Typography>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                      {bestInfo?.clap_count || 0}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Top Posts Section */}
            <Box mt={{ xs: 4, sm: 6 }}>
              <Typography variant="h6" gutterBottom>
                <Box display="flex" alignItems="center" gap={1}>
                  <ThumbsUp size={24} />
                  <span>の多い作品</span>
                </Box>
              </Typography>
              
              <Grid container spacing={{ xs: 2, sm: 3 }} mt={{ xs: 1, sm: 2 }}>
                {[
                  { id: bestInfo?.rank1, thumb: bestInfo?.rank1_thumb, title: bestInfo?.rank1_title },
                  { id: bestInfo?.rank2, thumb: bestInfo?.rank2_thumb, title: bestInfo?.rank2_title },
                  { id: bestInfo?.rank3, thumb: bestInfo?.rank3_thumb, title: bestInfo?.rank3_title }
                ].map((post, index) => (
                  post.id && (
                    <Grid item xs={12} sm={6} md={4} key={post.id}>
                      <Card 
                        component={Link} 
                        to={`/image/${post.id}`}
                        sx={{ 
                          textDecoration: 'none',
                          transition: '0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                          }
                        }}
                      >
                        <CardMedia
                          component="img"
                          height={isMobile ? 200 : 300}
                          image={`${ApiFetch.API_PROTOCOL}//${ApiFetch.IMG_HOST}/post/t/${post.thumb}_m.webp`}
                          alt={post.title}
                        />
                        <CardContent>
                          <Typography variant="body2" color="text.primary">
                            {post.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                ))}
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>{dialogConfig.title}</DialogTitle>
        <DialogContent>
          <Typography>{dialogConfig.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            キャンセル
          </Button>
          {dialogConfig.showConfirm && (
            <Button onClick={executeUnfollow} color="primary">
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default UserBest;